import { place, type RoomContext, type RoomDecorator } from '../index';
import * as kit from '../../castle-kit';
import * as g from '../../castle-kit-grounds';

/**
 * THE CRYPT (catacombs) — the cold, hushed undercroft of Castle Hearthhold, deliberately the DIMMEST,
 * SPARSEST beat on the whole map. Composition: three founders' SARCOPHAGI frame a memorial axis down the
 * centre; a SINGLE reverent altar + votive candle is the lone warm glow-pool a duck sits before in vigil;
 * everything else recedes into honest shadow. Bones and an urn-niche in the gloom, one lonely raven.
 * Less is more here — the empty dark floor IS the design: eerie, reverent, never cluttered.
 *
 * Geometry: zone (2800,2040) 440×600 → centre (3020,2340). Doors kept clear — N@x3020 (orchard),
 * W@y2170 (bailey), W@y2470 (treasury), E@x3240@y2340 (royal-tower). The central pew sit-anchor is
 * (3020,2340); only TWO diagonal wall torches light the edges so the room reads genuinely dark.
 */
const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { art } = ctx;
  const cx = 3020;
  const cy = 2340;

  // ── A dim crypt-floor runner, the memorial axis from the north tombs down to the vigil altar. ──
  place(ctx, kit.runnerRug(art, 96, 360), cx, cy);

  // ── HERO: the founders' SARCOPHAGI — three stone tombs framing the axis, the room's whole purpose. ──
  place(ctx, g.sarcophagus(art, 90), 2900, 2180); // NW tomb
  place(ctx, g.sarcophagus(art, 90), 3140, 2180); // NE tomb
  place(ctx, g.sarcophagus(art, 90), 2900, 2520); // SW tomb (cracked lid = the hidden-passage hint, by treasury)

  // ── A single urn alcove recessed into the east wall, receding into shadow. ──
  place(ctx, g.cryptNiche(art), 3180, 2340); // E wall, north of the royal-tower door

  // ── Scattered remains — two bone piles in the shadowed corners, no more. ──
  place(ctx, g.bonePile(art), 3160, 2520); // SE corner remains, balances the SW tomb
  place(ctx, g.bonePile(art), 2960, 2360);

  // ── SEAT + THE ONE WARM BEAT: the memorial pew (sit anchor 3020,2340) before a votive-lit altar — the
  //    crypt's single glow-pool, where a duck keeps silent vigil. Everything else stays dark. ──
  place(ctx, kit.altar(art), cx, 2560);        // reliquary altar on the south axis, the focus of the vigil
  place(ctx, kit.pew(art, 96), cx, cy);        // the vigil bench, dead-centre on the runner
  place(ctx, kit.votiveStand(art), cx, 2220);  // the lone candle, glowing up the axis toward the north tombs

  // ── Faint edge light: just TWO guttering wall torches on the diagonal so the room is dim, not dead. ──
  place(ctx, kit.wallTorch(art), 2820, 2080); // NW corner
  place(ctx, kit.wallTorch(art), 3220, 2600); // SE corner — also pools light onto the hidden-passage hint

  // ── A lone crypt raven keeps watch in the gloom — the cobwebbed, long-undisturbed undercroft vibe. ──
  place(ctx, kit.raven(art), 2880, 2620);
};

export default decorate;
